import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import NewsCard from './NewsCard';
import TopSectionNewsCard from './TopSectionNewsCard';
import ServicesController from "../hooks/useServicesController";

const NewsSection = ({ category }) => {
  const [news, setNews] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true); // Initialize as true
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchNews = async (pageNum = 1) => {
    if (isLoading) return;
    setIsLoading(true);
    setError(null);

    try {
      const response = await ServicesController.getServices(
        `news?category=${category}&page=${pageNum}&limit=5`
      );

      const newArticles = response || [];
      setNews(prev => pageNum === 1 ? newArticles : [...prev, ...newArticles]);
      // Check if fewer articles than the limit were returned
      setHasMore(newArticles.length === 5); // Assuming limit=5
      setPage(pageNum);
    } catch (err) {
      console.error('Axios fetch error:', err);
      setError(err.response?.data?.message || 'Failed to fetch news');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Reset news when category changes
    setNews([]);
    setPage(1);
    setHasMore(true);
    fetchNews(1);
    // eslint-disable-next-line
  }, [category]);

  const loadMore = () => {
    if (!hasMore || isLoading) return;
    fetchNews(page + 1);
  };

  return (
    <div className="mb-12">
      <h2 className="text-2xl font-bold text-gray-900 mb-6 capitalize">
        {category} News
      </h2>
      {error && <p className="text-red-500 text-center mb-4">Error: {error}</p>}
      {isLoading && news.length === 0 && (
        <p className="text-center text-gray-500">Loading...</p>
      )}
      {news.length > 0 && <TopSectionNewsCard {...news[0]} />}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        {news.slice(1).map((item) => (
          <NewsCard key={item.id} {...item} />
        ))}
      </div>
      <div className="text-center">
        {isLoading && news.length > 0 && (
          <p className="text-gray-500 mt-4">Loading more...</p>
        )}
        {hasMore && !isLoading && news.length > 0 && (
          <button
            onClick={loadMore}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-200 hover:to-indigo-400 hover:text-gray-500 text-white text-2xl font-semibold rounded-2xl shadow-2xl hover:shadow-3xl  transition-all duration-300 relative my-2"
          >
            Load More
          </button>
        )}
        {!hasMore && news.length > 0 && (
          <p className="text-gray-500 mt-4">No more articles to load</p>
        )}
      </div>
    </div>
  );
};

NewsSection.propTypes = {
  category: PropTypes.string.isRequired,
};

export default NewsSection;